export default class PermissionService {
  presets({
    terms
  }) {
    return [{
      id: 'open',
      label: 'More Open',
      description: `${terms.Lead}s and ${terms.Paralegal}s can see financial information across the firm including rates, invoices, and collections.`,
      permissions: {
        timekeeper: {
          include: ['time.createSelf', 'expenses.view', 'expenses.create', 'expenses.assignToExpense', 'charges.assignToCharge', 'matters.create', 'matters.edit', 'matters.billingInstructions', 'matters.clientNotes']
        },
        paralegal: {
          exclude: ['charges.lockTime', 'users.edit', 'users.create', 'matters.assign', 'matters.autoAssign', 'expenses.createHardCost']
        },
        lead: {
          exclude: ['users.create', 'matters.autoAssign', 'expenses.createHardCost', 'charges.lockTime']
        },
        principal: {
          exclude: ['matters.autoAssign']
        },
        operator: {
          exclude: ['matters.autoAssign']
        },
        accountant: {
          exclude: ['time.createSelf', 'matters.autoAssign']
        },
        primary: {
          exclude: ['matters.autoAssign', 'expenses.createHardCost', 'charges.lockTime']
        }
      }
    }, {
      id: 'balance',
      label: 'Balanced',
      description: `${terms.Lead}s can see revenue information for ${terms.matter}s they are responsible for. Full invoice workflow is available for operators, accountants and responsible ${terms.lead}s`,
      permissions: {
        timekeeper: {
          include: ['time.createSelf', 'expenses.view', 'expenses.create']
        },
        paralegal: {
          include: ['time.createSelf', 'time.createOthers', 'time.rates', 'expenses.view', 'expenses.create', 'matters.create', 'matters.edit', 'matters.billingInstructions', 'matters.clientNotes']
        },
        lead: {
          include: ['time.createSelf', 'time.createOthers', 'time.rates', 'expenses.view', 'expenses.create', 'matters.create', 'matters.edit', 'matters.rates', 'reports.view', 'reports.myrevenue']
        },
        principal: {
          exclude: ['billing.create', 'billing.submit', 'billing.pay', 'billing.email', 'trust.deposit', 'trust.pay', 'billing.distributions']
        },
        operator: {
          exclude: []
        },
        accountant: {
          exclude: ['time.createSelf', 'users.create']
        },
        primary: {
          exclude: ['expenses.createHardCost', 'charges.lockTime']
        }
      }
    }, {
      id: 'closed',
      label: 'More Closed',
      description: `${terms.Lead} can only see financial information for ${terms.matter}s they are responsible for. Invoice workflow is only available for operators and accountants, but responsible ${terms.lead}s have access to view and approve. Principals have access to financials across the firm.`,
      permissions: {
        timekeeper: {
          include: ['time.createSelf']
        },
        paralegal: {
          include: ['time.createSelf', 'time.createOthers', 'time.rates', 'expenses.view', 'expenses.create', 'matters.create', 'matters.edit', 'matters.billingInstructions', 'matters.clientNotes']
        },
        lead: {
          include: ['time.createSelf', 'time.createOthers', 'time.rates', 'expenses.view', 'expenses.create', 'matters.create', 'matters.edit', 'matters.billingInstructions', 'matters.clientNotes', 'reports.view', 'reports.myrevenue']
        },
        principal: {
          exclude: ['billing.create', 'billing.submit', 'billing.pay', 'billing.email', 'billing.distributions', 'trust.deposit', 'trust.pay']
        },
        operator: {
          exclude: []
        },
        accountant: {
          exclude: ['time.createSelf', 'users.create']
        },
        primary: {
          exclude: ['billing.create', 'billing.submit', 'billing.pay', 'billing.email', 'billing.distributions', 'trust.deposit', 'trust.pay', 'expenses.createHardCost', 'charges.lockTime']
        }
      }
    }];
  }
  permissions({
    terms,
    features
  }) {
    const time = {
      id: 'time',
      label: 'Time Entry',
      permissions: [{
        id: 'createSelf',
        locked: true,
        exclude: 'accountant',
        label: 'Time for Self'
      }, {
        id: 'createOthers',
        label: 'Time for Others'
      }, {
        id: 'viewRates',
        label: 'View Rates'
      }, {
        id: 'editRates',
        label: 'Edit Rates'
      }]
    };
    const expenses = {
      id: 'expenses',
      label: 'Expenses',
      permissions: [{
        id: 'view',
        label: 'View Expenses'
      }, {
        id: 'create',
        label: 'Create/Edit Expenses'
      }, {
        id: 'assign',
        label: 'Assign QuickBooks Expenses'
      }, {
        id: 'assignToExpense',
        label: 'Can be Assigned to Expense'
      }, {
        id: 'createHardCost',
        label: 'Create Hard Costs',
        description: 'Allows posting a hard cost directly to QuickBooks as a Bill or a Check.'
      }]
    };
    const charges = {
      id: 'charges',
      label: 'Fixed Fees',
      permissions: [{
        id: 'create',
        label: 'Create/Edit Fixed Fees'
      }, {
        id: 'assignToCharge',
        label: 'Can be Assigned to Fixed Fees'
      }]
    };
    const matters = {
      id: 'matters',
      label: `Clients and ${terms.Matter}s`,
      permissions: [{
        id: 'create',
        label: `Create and Edit Clients/${terms.Matter}s`,
        exclude: 'primary'
      }, {
        id: 'rates',
        label: `View Rates`
      }, {
        id: 'assign',
        label: 'Edit Rates and Users'
      }, {
        id: 'autoAssign',
        label: `Auto-Assign to ${terms.Matter}s`,
        description: `Allows creating time entries for any ${terms.matter}. Creating a time entry for a ${terms.matter} and user will automatically assign that user to the ${terms.matter}, if they weren't already assigned`
      }, {
        id: 'accounting',
        label: 'Connect/Create in QuickBooks'
      }, {
        id: 'clientNotes',
        label: 'View Client Notes'
      }, {
        id: 'billingInstructions',
        label: 'View Billing Instructions'
      }]
    };
    const billing = {
      id: 'billing',
      label: 'Billing',
      permissions: [{
        id: 'view',
        label: 'View Invoices'
      }, {
        id: 'create',
        label: 'Create Draft Invoices'
      }, {
        id: 'edit',
        label: 'Edit Draft Invoices'
      }, {
        id: 'approve',
        label: 'Approve Invoices'
      }, {
        id: 'submit',
        label: 'Submit Invoices to QuickBooks'
      }]
    };
    const trust = {
      id: 'trust',
      label: 'Trust',
      permissions: [{
        id: 'view',
        label: 'View Trust Account(s)'
      }, {
        id: 'deposit',
        label: 'Trust Deposits and Payments'
      }]
    };
    const reports = {
      id: 'reports',
      label: 'Reports',
      permissions: [{
        id: 'view',
        label: 'General Reports'
      }, {
        id: 'myrevenue',
        label: 'My Reports'
      }, {
        id: 'revenueAttorney',
        label: `Revenue by ${terms.Lead} Report`
      }, {
        id: 'revenue',
        label: 'Other Revenue Reports'
      }, {
        id: 'billable',
        label: 'Billable Reports'
      }]
    };
    // Only the row is flagged; PermissionConfig.cs defaults Charges.LockTime for every
    // firm, so a firm flagged later already has Principal/Operator holding it.
    if (features.fixedFeeTimeLock) {
      charges.permissions.push({
        id: 'lockTime',
        label: 'Can lock and unlock time on fixed fees'
      });
    }
    if (features.distribution) {
      billing.permissions.push({
        id: 'distributions',
        label: `Manage Distributions`
      });
    }
    const beta = 'This relates to a feature that might not be available in your account yet.';
    reports.permissions.push({
      id: 'viewDashboards',
      label: `Overview Tab`,
      description: beta
    });
    reports.permissions.push({
      id: 'chat',
      label: `ClerkAI`,
      description: beta
    });
    return [time, expenses, charges, matters, billing, trust, reports];
  }
}